import clsx from "clsx";
import { ForwardedRef, forwardRef, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import {
  getGroupClassNames,
  getLabelsClassNames,
  getRadioButtonClassNames,
  getRadioCardClassNames,
} from "./class-names";
import { Label } from "../label";

export type RadioButtonOptionItem = {
  label: string;
  subtitle?: string;
  detail?: ReactNode;
  prefix?: ReactNode;
  disabled?: boolean;
  labelOverride?: ReactNode;
  value: string;
};

export type RadioButtonsProps = {
  className?: string;
  options: RadioButtonOptionItem[];
  value: string | undefined;
  onChange: (value: string) => void;
  id?: string;
  name?: string;
  disabled?: boolean;
  error?: boolean;
  readOnly?: boolean;
  box?: boolean;
  direction?: "row" | "column";
  align?: "start" | "end" | "center";
  label?: string;
  mandatory?: boolean;
  message?: string;
  onClearClick?: () => void;
  withLabelWrapper?: boolean;
  dataTestId?: string;
};

const RadioGroupWrapper = ({
  withLabel = true,
  children,
  label,
  mandatory,
  message,
  readOnly,
  id,
  error,
}: {
  withLabel: boolean;
  children: ReactNode;
  label?: string;
  mandatory?: boolean;
  message?: string;
  id?: string;
  error?: boolean;
  readOnly?: boolean;
}) => {
  if (!withLabel) return children;

  return (
    <Label
      text={label}
      mandatory={mandatory}
      disabled={readOnly}
      name={id}
      error={!!error}
      message={message}
    >
      {children}
    </Label>
  );
};

const BaseRadioButtons = (
  {
    className,
    options,
    value,
    onChange,
    id,
    name,
    error,
    disabled,
    readOnly,
    box,
    direction,
    align,
    label,
    mandatory,
    message,
    onClearClick,
    withLabelWrapper = true,
    dataTestId,
  }: RadioButtonsProps,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const labelsClassNames = getLabelsClassNames({
    disabled,
    box,
  });

  return (
    <RadioGroupWrapper
      withLabel={withLabelWrapper}
      label={label}
      mandatory={mandatory}
      message={message}
      readOnly={readOnly}
      id={id}
      error={error}
    >
      <div
        id={id}
        className={twMerge(
          clsx(getGroupClassNames({ align, direction }), className),
        )}
      >
        {options.map((o, idx) => (
          <label
            key={o.value}
            tabIndex={-1}
            aria-disabled={disabled || o.disabled}
            className={twMerge(
              getRadioCardClassNames({
                readOnly,
                disabled: disabled || o.disabled,
                box,
                error,
              }),
            )}
          >
            <input
              data-testid={dataTestId}
              id={id && `${id}-${o.value}`}
              ref={idx === 0 ? ref : undefined}
              name={name}
              type="radio"
              value={o.value}
              checked={o.value === value}
              onChange={(e) => !readOnly && onChange(e.currentTarget.value)}
              disabled={disabled || o.disabled}
              className="appearance-none outline-none"
              onClick={(e) => {
                if (onClearClick && o.value === value) {
                  e.preventDefault();
                  onClearClick();
                }
              }}
            />
            <div
              className={twMerge(
                getRadioButtonClassNames({
                  checked: o.value === value,
                  disabled: disabled || o.disabled,
                  error,
                }),
              )}
            />
            {o.labelOverride || (
              <div
                className={
                  getLabelsClassNames({
                    disabled: disabled || o.disabled,
                    box,
                  }).labelGroupClassNames
                }
              >
                {o.prefix && (
                  <div className="flex items-center">{o.prefix}</div>
                )}
                <div className="flex items-center">
                  <div className={labelsClassNames.titleClassNames}>
                    {o.label}
                  </div>
                  {o.subtitle && <div>{o.subtitle}</div>}
                </div>
                {o.detail && (
                  <div className="flex items-center">{o.detail}</div>
                )}
              </div>
            )}
          </label>
        ))}
      </div>
    </RadioGroupWrapper>
  );
};

export const RadioButtons = forwardRef(BaseRadioButtons);
